import React from "react";

const AccountDeletion360Vitals = () => {
  const appSteps = [
    { number: "1", icon: "📱", label: "Open the 360Vitals app" },
    { number: "2", icon: "👤", label: "Go to Profile / Settings" },
    { number: "3", icon: "🗑️", label: 'Tap "Delete Account"' },
    { number: "4", icon: "✅", label: "Confirm your request" },
  ];

  const deletedData = [
    { icon: "🔐", label: "Account and authentication data" },
    { icon: "👤", label: "Personal profile information" },
    { icon: "🏃", label: "Health data (sleep, water, calories)" },
    { icon: "🤖", label: "AI Coach history and recommendations" },
  ];

  const retainedReasons = [
    { icon: "⚖️", label: "Legal obligations" },
    { icon: "🛡️", label: "Fraud prevention" },
    { icon: "💾", label: "System backups (automatically cleared later)" },
  ];

  const sections = [
    {
      number: "1",
      title: "Introduction",
      content: (
        <p className="text-gray-700 text-base leading-relaxed">
          At <strong>360Vitals</strong>, we respect your control over your data. This page
          explains how you can delete your account and what happens to your data after
          deletion.
        </p>
      ),
    },
    {
      number: "2",
      title: "How to Request Account Deletion",
      content: (
        // ✅ Inline style guarantees two-column row always
        <div style={{ display: "grid", gap: "24px" }}>
          {/* From the App */}
          <div>
            <p className="text-gray-700 font-medium" style={{ marginBottom: "8px" }}>
              📱 From the App
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {appSteps.map((step) => (
                <div
                  key={step.number}
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      flexShrink: 0,
                      width: "22px",
                      height: "22px",
                      backgroundColor: "#ef4444",
                      color: "white",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    {step.number}
                  </div>
                  <span style={{ fontSize: "13px" }}>{step.icon}</span>
                  <p style={{ margin: 0, fontSize: "13px", color: "#374151" }}>
                    {step.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Via Web Request */}
          <div>
            <p className="text-gray-700 font-medium" style={{ marginBottom: "8px" }}>
              🌐 Via Web Request
            </p>
            <p style={{ fontSize: "13px", color: "#4b5563", marginBottom: "8px" }}>
              If you are unable to access the app, request deletion by emailing us at{" "}
              <a
                href="mailto:projects@datadna.in"
                style={{ color: "#dc2626", fontWeight: 600, textDecoration: "underline" }}
              >
                projects@datadna.in
              </a>{" "}
              with:
            </p>
            <ul style={{ paddingLeft: "18px", margin: 0, fontSize: "13px", color: "#4b5563" }}>
              <li>Your registered email ID</li>
              <li>
                Subject: <strong>Account Deletion Request</strong>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      number: "3",
      title: "What Happens When You Delete",
      content: (
        <>
          <p style={{ fontSize: "14px", color: "#4b5563", marginBottom: "8px" }}>
            Once your request is processed, the following will be permanently removed:
          </p>
          {/* ✅ Same inline style two-column grid */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {deletedData.map((item, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#ef4444",
                    borderRadius: "50%",
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: "16px", flexShrink: 0 }}>{item.icon}</span>
                <p style={{ margin: 0, fontSize: "14px", color: "#374151" }}>
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      number: "4",
      title: "⏳ Data Retention Timeline",
      content: (
        <>
          <p style={{ fontSize: "14px", color: "#374151", marginBottom: "8px" }}>
            Most data is deleted within{" "}
            <strong style={{ color: "#dc2626" }}>7–14 days</strong> of your deletion
            request. Some data may be temporarily retained for:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {retainedReasons.map((item, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#9ca3af",
                    borderRadius: "50%",
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: "16px", flexShrink: 0 }}>{item.icon}</span>
                <p style={{ margin: 0, fontSize: "14px", color: "#4b5563" }}>
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      number: "5",
      title: "⚠️ Irreversibility",
      content: (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg">
          <p className="text-gray-700 text-base leading-relaxed">
            Account deletion is{" "}
            <strong className="text-red-600">permanent and cannot be undone</strong>. You
            will not be able to recover your data, history, or preferences once deleted.
          </p>
        </div>
      ),
    },
    {
      number: "6",
      title: "Active Subscriptions",
      content: (
        <>
          <p className="text-gray-600 text-base mb-3">
            Before deleting your account, please cancel any active subscriptions via:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>Google Play Store</li>
            <li>Apple App Store</li>
          </ul>
          <p className="text-gray-600 text-base mt-3">
            We are <strong>not responsible</strong> for ongoing subscription charges if
            not cancelled beforehand.
          </p>
        </>
      ),
    },
    {
      number: "7",
      title: "Exceptions",
      content: (
        <>
          <p className="text-gray-600 text-base mb-3">
            We may retain limited information if required to:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>Comply with legal obligations</li>
            <li>Resolve disputes</li>
            <li>Enforce agreements</li>
          </ul>
        </>
      ),
    },
    {
      number: "8",
      title: "📧 Need Assistance?",
      content: (
        <>
          <p className="text-gray-600 text-base mb-2">
            If you face any issues deleting your account, contact us at:
          </p>
          <a
            href="mailto:projects@datadna.in"
            className="text-red-600 hover:text-red-800 underline font-semibold text-base"
          >
            projects@datadna.in
          </a>
        </>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-red-600 to-red-400 py-14 px-4 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
          Account Deletion
        </h1>
        <p className="text-red-100 text-base sm:text-lg font-medium">360Vitals App</p>
      </div>

      {/* Intro Card */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8 border-l-4 border-red-500">
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            At <strong>360Vitals</strong>, we respect your control over your data. Deleting
            your account is <strong>permanent</strong> and cannot be undone. Please read
            below before proceeding.
          </p>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {sections.map((section, index) => (
          <React.Fragment key={section.number}>
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                {section.number}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3">
                  {section.title}
                </h2>
                {section.content}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Footer */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-red-50 p-6 text-center border">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} DATADNA · 360Vitals App · All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountDeletion360Vitals;